import { forwardRef, useMemo } from 'react';
import type { SelectHTMLAttributes } from 'react';
import { Select } from '@/components/ui/Select';
import { useAllPrimarii, useEntitatiSilvice, useOcoaleSilvice } from './useRegions';

type Kind = 'entitateSilvica' | 'ocolSilvic' | 'primarie' | 'raion';

interface RegionsComboboxProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  kind: Kind;
  /**
   * The current field value. When provided and not in the reference
   * option list (e.g. legacy non-canonical data), it's prepended as a
   * "(current: …)" option so the select stays in sync with the form
   * state. RHF callers can pass `defaultValue={record?.<field>}` for
   * edit dialogs.
   */
  defaultValue?: string;
}

/**
 * Strict region picker (raion / primărie / ocol silvic / entitate
 * silvică). Suggestions come from the regions reference tables — the
 * single source of truth — so users can't introduce duplicate
 * spellings via the form. Free-text was deliberately ruled out across
 * web + mobile so the canonical name from regions.xlsx is the only
 * thing landing in the DB.
 *
 * Forwards the ref so RHF `register(...)` works without wrapping in
 * Controller. Drop-in compatible with the previous combobox API.
 */
export const RegionsCombobox = forwardRef<
  HTMLSelectElement,
  RegionsComboboxProps
>(({ kind, defaultValue, ...rest }, ref) => {
  const options = useOptions(kind);
  const currentInList = useMemo(
    () =>
      defaultValue
        ? options.some((o) => o.value === defaultValue)
        : true,
    [options, defaultValue],
  );

  return (
    <Select ref={ref} defaultValue={defaultValue ?? ''} {...rest}>
      <option value="">—</option>
      {/* Legacy / out-of-reference value bridge: if the form holds a
          stored value that isn't in the reference table (e.g. an
          un-normalized historical entry), surface it so the select
          can faithfully render it. Once the user picks a canonical
          option this synthetic entry disappears on next mount. */}
      {defaultValue && !currentInList ? (
        <option value={defaultValue}>{defaultValue} (curent)</option>
      ) : null}
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </Select>
  );
});
RegionsCombobox.displayName = 'RegionsCombobox';

interface ComboOption {
  value: string;
  label: string;
}

function useOptions(kind: Kind): ComboOption[] {
  const entitati = useEntitatiSilvice();
  const ocoale = useOcoaleSilvice();
  const primarii = useAllPrimarii();

  return useMemo(() => {
    if (kind === 'entitateSilvica') {
      // Match the canonical form the server stores — which is the
      // reference name VERBATIM (each row in regions.xlsx already
      // includes its authoritative prefix: ÎSÎS / ÎSC / ÎSSC / RN /
      // Agentia / Icas). The cod is surfaced only as a hint in the
      // label so the picker is browseable without the cod taking
      // over the displayed value.
      return (entitati.data?.items ?? []).map((e) => ({
        value: e.name,
        label: `${e.cod} — ${e.name}`,
      }));
    }
    if (kind === 'ocolSilvic') {
      return (ocoale.data?.items ?? []).map((o) => ({
        value: o.cod,
        label: `${o.cod} (${o.entitateSilvicaCod})`,
      }));
    }
    if (kind === 'raion') {
      // Raions aren't a standalone reference table — they're a field
      // on Primarie. Derive distinct raioane from the loaded primarii.
      const seen = new Set<string>();
      for (const p of primarii.data?.items ?? []) {
        if (p.raion) seen.add(p.raion);
      }
      return Array.from(seen)
        .sort((a, b) => a.localeCompare(b, 'ro'))
        .map((r) => ({ value: r, label: r }));
    }
    // primarie — write the parsed name; show "name — raion · cod" so
    // the user can disambiguate same-named primarii in different raioane.
    return (primarii.data?.items ?? []).map((p) => ({
      value: p.name,
      label: `${p.name} — ${p.raion} · ${p.cod}`,
    }));
  }, [kind, entitati.data, ocoale.data, primarii.data]);
}
